import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import { FormControlLabel, Grid, Typography } from '@material-ui/core';
import Toggle from '@material-ui/core/Switch';
import Dialog from '@material-ui/core/Dialog';
import Drawer from '@material-ui/core/Drawer';
import { Link as NavLink, Route, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Spin from 'components/common/Spin';
import { DragDropContext } from 'react-beautiful-dnd';
import { LISTS } from './index';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  DELETE_SCHOOL,
  GET_SCHOOLS,
  UPDATE_SCHOOL,
} from 'graphql/student_college';
import SchoolList from './components/SchoolList';
import useSnack from 'hooks/useSnack';

import AddSchool from './components/AddSchool';

import SchoolDetails from './School';
import PRODUCTS from 'constant/product-types';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const useStyles = makeStyles((theme) => ({
  track: {
    background: theme.palette.text.secondary,
  },
  board: {
    [theme.breakpoints.down('md')]: {
      flexWrap: 'nowrap',
      overflowX: 'scroll',
    },
  },
}));

const SchoolListView = ({ student, match, history, onAddSchool }) => {
  const classes = useStyles();
  const snack = useSnack();
  const user = useSelector((state) => state.user.user);
  const [showScores, toggleScores] = React.useState(false);
  const [showDeadlines, toggleDeadlines] = React.useState(false);
  const [updateSchool] = useMutation(UPDATE_SCHOOL);
  const [removeSchool] = useMutation(DELETE_SCHOOL);
  const queryParams = { student_sub: student.sub };
  const { loading, error, data } = useQuery(GET_SCHOOLS, {
    variables: queryParams,
  });
  const isStudent = user.type === 'student';
  const routePrefix = isStudent ? '' : `/students/${student.id}`;
  const schools = data ? data.student_college : [];
  const [addCard, toggleAddCard] = React.useState(false);
  const [type, setType] = React.useState(false);

  const handleScoreDisplay = (event) => {
    toggleScores(event.target.checked);
  };

  const handleDeadlineDisplay = (e) => {
    toggleDeadlines(e.target.checked);
  };

  const onRemoveSchool = (id) => {
    return removeSchool({
      variables: {
        id,
      },
      update: (store) => {
        const data = store.readQuery({
          query: GET_SCHOOLS,
          variables: queryParams,
        });
        const updatedData = data.student_college.filter((i) => i.id !== id);
        store.writeQuery({
          query: GET_SCHOOLS,
          variables: queryParams,
          data: {
            student_college: updatedData,
          },
        });
      },
    }).then(
      () => {
        window.analytics.track('School removed from School List', {
          id,
          student,
          product: PRODUCTS_LOOKUP[student.product_type],
          user,
        });
        history.goBack();
        snack.success('School deleted');
      },
      (e) => snack.error(e.message),
    );
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    // dropped nowhere
    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      // reordering
      if (source.index === destination.index) {
        return;
      }
      // TODO update the order
    } else {
      const id = Number(draggableId);
      const school = schools.find((i) => i.id === id);
      updateSchool({
        variables: {
          id,
          input: {
            category: destination.droppableId,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          row: {
            ...school,
            category: destination.droppableId,
          },
        },
      }).then(
        () => {
          window.analytics.track(
            `School category changed to ${destination.droppableId}`,
            {
              ...school,
              category: destination.droppableId,
              student,
              product: PRODUCTS_LOOKUP[student.product_type],
              user,
            },
          );
          snack.success('Please add a reason for change.');
          history.push(`${match.url}/${school.college_id}?tab=2`);
        },
        (e) => snack.error(e.message),
      );
    }
  };

  const sat_scores =
    student?.highschool_info?.sat_scores &&
    student?.highschool_info?.sat_scores.sort((a, b) => {
      return moment(a.date).diff(b.date);
    });
  // console.log(sat_scores);
  const total_scores = sat_scores?.map(
    ({ math, rw }) => parseInt(math) + parseInt(rw),
  );
  // console.log(total_scores);
  const indexOfMaxValue = total_scores?.reduce(
    (iMax, x, i, arr) => (x > arr[iMax] ? i : iMax),
    0,
  );
  // console.log('index of max value : ', indexOfMaxValue);
  // const math_scores = sat_scores.map(({ math }) => parseInt(math));
  // console.log(math_scores);
  // const rw_scores = sat_scores.map(({ rw }) => parseInt(rw));

  const act_scores =
    student?.highschool_info?.act_scores &&
    student?.highschool_info?.act_scores.sort((a, b) => {
      return moment(a.date).diff(b.date);
    });

  return (
    <Box>
      <Helmet title="Schools" />
      <Box
        display="flex"
        justifyContent="flex-end"
        marginBottom={3}
        marginTop={{ xs: 2, sm: 0 }}
      >
        <FormControlLabel
          style={{ marginRight: 16 }}
          labelPlacement="start"
          control={
            <Toggle
              checked={showScores}
              onChange={handleScoreDisplay}
              name="showScores"
              color="primary"
              classes={{
                track: classes.track,
              }}
            />
          }
          label={
            <Typography color="textSecondary">
              {' '}
              {!showScores ? 'View Scores' : 'Hide Scores'}
            </Typography>
          }
        />
        <FormControlLabel
          labelPlacement="start"
          control={
            <Toggle
              checked={showDeadlines}
              onChange={handleDeadlineDisplay}
              name="showDeadlines"
              color="primary"
              classes={{
                track: classes.track,
              }}
            />
          }
          label={
            <Typography color="textSecondary">
              {' '}
              {!showDeadlines ? 'View Deadlines' : 'Hide Deadlines'}
            </Typography>
          }
        />
      </Box>
      <Box display={'flex'} flexWrap="wrap" marginBottom={2.5}>
        <Box marginRight={3}>
          <NavLink to={`${routePrefix}/profile/school`}>
            <Typography variant="caption">My SAT Math: </Typography>
          </NavLink>
          <Typography variant="body2" color="textPrimary" component="span">
            {sat_scores?.length > 0 ? sat_scores[indexOfMaxValue].math : 'N/A'}
          </Typography>
        </Box>
        <Box marginRight={3}>
          <NavLink to={`${routePrefix}/profile/school`}>
            <Typography variant="caption">My SAT Critical Reading: </Typography>
          </NavLink>
          <Typography variant="body2" color="textPrimary" component="span">
            {sat_scores?.length > 0 ? sat_scores[indexOfMaxValue].rw : 'N/A'}
          </Typography>
        </Box>
        <Box marginRight={3}>
          <NavLink to={`${routePrefix}/profile/school`}>
            <Typography variant="caption">My ACT Score: </Typography>
          </NavLink>
          <Typography variant="body2" color="textPrimary" component="span">
            {act_scores?.length > 0 &&
            act_scores[act_scores?.length - 1]?.overall
              ? act_scores[act_scores?.length - 1].overall
              : 'N/A'}
          </Typography>
        </Box>
        <Box>
          <NavLink to={`${routePrefix}/profile/school`}>
            <Typography variant="caption">My Unweighted GPA: {''}</Typography>
          </NavLink>
          <Typography variant="body2" color="textPrimary" component="span">
            {student?.highschool_info?.current_gpa_unweighted
              ? student?.highschool_info?.current_gpa_unweighted
              : 'N/A'}
          </Typography>
        </Box>
      </Box>
      {loading ? (
        <Spin />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Spin spinning={loading} linear>
            <Box>
              <Grid container spacing={1} className={classes.board}>
                {LISTS.map((i) => (
                  <Grid item xs={12} md={3} key={i.index}>
                    <SchoolList
                      setType={setType}
                      toggleCard={toggleAddCard}
                      showScores={showScores}
                      showDeadlines={showDeadlines}
                      schools={schools.filter((s) => s.category === i.type)}
                      {...i}
                      student={student}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Spin>
        </DragDropContext>
      )}

      {addCard && (
        <Dialog
          maxWidth="sm"
          fullWidth
          open
          onClose={() => toggleAddCard(false)}
        >
          <AddSchool
            category={LISTS.find((i) => i.type === type)}
            onAddSchool={onAddSchool}
            onClose={() => toggleAddCard(false)}
          />
        </Dialog>
      )}

      <Route
        exact
        path={`${routePrefix}/schools/list/:id`}
        render={(props) => (
          <Drawer anchor="right" open onClose={() => props.history.goBack()}>
            <SchoolDetails
              onClose={() => props.history.goBack()}
              onDelete={onRemoveSchool}
              collegeId={props.match.params.id}
              school={schools.find(
                (i) => i.college_id === props.match.params.id,
              )}
              {...props}
            />
          </Drawer>
        )}
      />
    </Box>
  );
};

export default SchoolListView;
